import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import type { InfoGateField, WorkflowConfig } from '@kfolks/types'
import { RequestWorkflowTimeline } from '@/components/RequestWorkflowTimeline'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { type RequestInfoGateDTO, requestCenterApi } from '@/services/request-center-api'

/**
 * `/requests-center/approvals/:id` — approve / reject actions.
 *
 * Rejection requires a non-empty comment (backend enforces; we mirror the
 * rule on the button's `disabled` state). Approve succeeds → redirect to
 * the parent request so the submitter can see the advance. Reject
 * succeeds → stay on the inbox landing so the approver can work the queue.
 * 404 on load → show the friendly "not assigned to you" message instead
 * of a raw error.
 */
export function ApprovalDetailPage() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [comment, setComment] = useState('')

	const {
		data: approval,
		isLoading,
		error,
	} = useQuery({
		queryKey: ['approval', id],
		queryFn: () => requestCenterApi.getApproval(id ?? ''),
		enabled: Boolean(id),
	})

	const requestId = approval?.requestId
	// Every persisted approval row for the parent request, ordered by step.
	const { data: timeline } = useQuery({
		queryKey: ['approvals-timeline', requestId],
		queryFn: () => requestCenterApi.listApprovalsByRequest(requestId ?? ''),
		enabled: Boolean(requestId),
	})
	// Open info-gates on the parent request — needed to find this approval's
	// own side-loop gate id when the SLA is paused (so we can render the
	// "Withdraw" button). Empty for callers without read scope.
	const { data: pendingGates } = useQuery({
		queryKey: ['pending-info-gates', requestId],
		queryFn: () => requestCenterApi.listPendingInfoGates(requestId ?? ''),
		enabled: Boolean(requestId),
	})
	const openInfoGate = (pendingGates ?? []).find((g) => g.parentApprovalId === approval?.id) ?? null

	// Answered side-loop info-gate (full payload). Only fetched when the
	// approval DTO already carries the `answeredInfoGate` summary — that's
	// the cheap signal that there's something to render. The full payload
	// (`providedData` + `fieldsSnapshot`) backs the "Information provided"
	// card so the approver doesn't have to navigate to the parent request
	// to see what was answered.
	const { data: answeredInfoGate } = useQuery({
		queryKey: ['answered-info-gate', approval?.id],
		queryFn: () => requestCenterApi.getAnsweredInfoGate(approval?.id ?? ''),
		enabled: Boolean(approval?.id && approval?.answeredInfoGate),
	})

	// Workflow config — the authoritative step list (labels + step ids for
	// every future step, not just the persisted ones). Mirrors the pattern
	// on RequestDetailPage: list all types once (cached 5 min) and resolve
	// client-side by slug. Cheap (catalogue is ~10 rows).
	const { data: allTypes } = useQuery({
		queryKey: ['request-types', { includeInactive: true }],
		queryFn: () => requestCenterApi.listRequestTypes({ includeInactive: true }),
		staleTime: 5 * 60 * 1000,
	})
	const requestType = allTypes?.find((t) => t.slug === approval?.request.typeSlug) ?? null
	const workflowConfig = (requestType?.workflowConfig ?? null) as WorkflowConfig | null

	// All three invalidations on approve / reject — keeps the request
	// detail page (state badge), the tickets list (post-final-approve a
	// new row appears), and the audit timeline (new approval / request
	// events) in sync with the new server state. Without these, the UI
	// only refreshes on a hard reload (Spec 3 smoke #2).
	const invalidateRequestSurfaces = (rid: string | null | undefined) => {
		if (!rid) return
		void queryClient.invalidateQueries({ queryKey: ['request', rid] })
		void queryClient.invalidateQueries({ queryKey: ['tickets-by-request', rid] })
		void queryClient.invalidateQueries({ queryKey: ['request-audit', rid] })
		void queryClient.invalidateQueries({ queryKey: ['comments', 'request_center_request', rid] })
	}

	const approveMutation = useMutation({
		mutationFn: () => requestCenterApi.approveApproval(id ?? '', comment || undefined),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['approval', id] })
			// Also bust the inbox list cache — otherwise a user going back to
			// /requests-center/approvals sees the row they just decided still
			// listed as pending until the query times out.
			void queryClient.invalidateQueries({ queryKey: ['approvals-inbox'] })
			void queryClient.invalidateQueries({ queryKey: ['approvals-timeline', requestId] })
			invalidateRequestSurfaces(approval?.requestId)
			if (approval?.requestId) void navigate(`/requests-center/${approval.requestId}`)
		},
	})

	const rejectMutation = useMutation({
		mutationFn: () => requestCenterApi.rejectApproval(id ?? '', comment),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['approval', id] })
			void queryClient.invalidateQueries({ queryKey: ['approvals-inbox'] })
			void queryClient.invalidateQueries({ queryKey: ['approvals-timeline', requestId] })
			invalidateRequestSurfaces(approval?.requestId)
			void navigate('/requests-center/approvals')
		},
	})

	// Side-loop SLA-paused invalidation set: same surfaces as approve/reject
	// PLUS the pending-info-gates query (the source of `openInfoGate` and
	// the submitter's answer-form state) and the answered-info-gate query
	// (so the "Information provided" card refreshes — or clears — after
	// the gate is withdrawn / superseded).
	const invalidateAfterInfoAction = () => {
		void queryClient.invalidateQueries({ queryKey: ['approval', id] })
		void queryClient.invalidateQueries({ queryKey: ['approvals-inbox'] })
		void queryClient.invalidateQueries({ queryKey: ['approvals-timeline', requestId] })
		void queryClient.invalidateQueries({ queryKey: ['pending-info-gates', requestId] })
		void queryClient.invalidateQueries({ queryKey: ['answered-info-gate', id] })
		invalidateRequestSurfaces(approval?.requestId)
	}

	const requestInfoMutation = useMutation({
		mutationFn: (payload: { fields: InfoGateField[]; note?: string }) =>
			requestCenterApi.requestInfoOnApproval(id ?? '', payload),
		onSuccess: invalidateAfterInfoAction,
	})

	const cancelInfoMutation = useMutation({
		mutationFn: (infoGateId: string) => requestCenterApi.cancelInfoRequest(infoGateId),
		onSuccess: invalidateAfterInfoAction,
	})

	if (!id) return <ErrorState message="Missing approval id." />
	if (isLoading) return <DetailSkeleton />
	if (error) {
		const status = isAxiosError(error) ? error.response?.status : undefined
		const msg =
			status === 404 ? 'This approval is not assigned to you (or has been decided).' : 'Failed to load the approval.'
		return <ErrorState message={msg} />
	}
	if (!approval) return <ErrorState message="Approval not found or not assigned to you." />

	const isPending = approval.status === 'pending'
	const isPaused = Boolean(approval.slaPausedAt)
	const busy =
		approveMutation.isPending ||
		rejectMutation.isPending ||
		requestInfoMutation.isPending ||
		cancelInfoMutation.isPending
	const rejectDisabled = busy || comment.trim().length === 0

	const stepLabel = approval.stepLabel ?? approval.stepName
	return (
		<div className="mx-auto max-w-2xl space-y-6">
			<header className="space-y-2">
				<div className="flex flex-wrap items-center gap-3">
					<h1 className="text-2xl font-semibold tracking-tight">{stepLabel}</h1>
					<Badge>{approval.status}</Badge>
					<Badge variant="outline">
						Step {approval.stepOrder}/{approval.totalSteps}
					</Badge>
					<Badge variant="outline">{approval.request.priority}</Badge>
				</div>
				<p className="text-sm text-muted-foreground">
					<span className="font-medium">{approval.request.typeName}</span>{' '}
					<Link className="underline" to={`/requests-center/${approval.requestId}`}>
						{approval.request.requestNumber}
					</Link>{' '}
					· submitted by {approval.request.submitterDisplayName}
				</p>
			</header>

			<Card>
				<CardHeader>
					<CardTitle className="text-base">Workflow timeline</CardTitle>
				</CardHeader>
				<CardContent>
					<RequestWorkflowTimeline
						workflowConfig={workflowConfig}
						persistedRows={timeline?.data ?? []}
						currentApprovalId={approval.id}
						totalStepsFallback={approval.totalSteps}
					/>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-base">Approval details</CardTitle>
				</CardHeader>
				<CardContent>
					<dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
						<DescTerm
							label="Explicit approvers"
							value={approval.approverDisplayNames.length ? approval.approverDisplayNames.join(', ') : '—'}
						/>
						<DescTerm
							label="Approver groups"
							value={approval.approverGroupNames.length ? approval.approverGroupNames.join(', ') : '—'}
						/>
						<DescTerm label="Resolved manager" value={approval.resolvedManagerDisplayName ?? '—'} />
						<DescTerm label="Due at" value={approval.dueAt ? new Date(approval.dueAt).toLocaleString() : '—'} />
					</dl>
				</CardContent>
			</Card>

			{isPending && !isPaused && approval.answeredInfoGate ? (
				<AnsweredInfoCard
					summary={approval.answeredInfoGate}
					full={answeredInfoGate ?? null}
					dataTestId="answered-info-card"
				/>
			) : null}

			{isPending && isPaused && openInfoGate ? (
				<PausedActionsCard
					openInfoGate={openInfoGate}
					onWithdraw={() => cancelInfoMutation.mutate(openInfoGate.id)}
					isWithdrawing={cancelInfoMutation.isPending}
					error={cancelInfoMutation.error}
				/>
			) : isPending ? (
				<DecideCard
					comment={comment}
					setComment={setComment}
					busy={busy}
					rejectDisabled={rejectDisabled}
					onApprove={() => approveMutation.mutate()}
					onReject={() => rejectMutation.mutate()}
					onRequestInfo={(payload) => requestInfoMutation.mutate(payload)}
					approveError={approveMutation.error}
					rejectError={rejectMutation.error}
					requestInfoError={requestInfoMutation.error}
					approving={approveMutation.isPending}
					rejecting={rejectMutation.isPending}
					requestingInfo={requestInfoMutation.isPending}
				/>
			) : (
				<Card>
					<CardContent className="pt-6 text-sm text-muted-foreground">
						This approval has already been {approval.status}. No further action available.
					</CardContent>
				</Card>
			)}
		</div>
	)
}

// ── Paused state: open side-loop info-request ───────────────────────────────

/**
 * Surfaced when this approval has an open info-request (`slaPausedAt` set
 * AND a matching pending gate). Approve / Reject are hidden until the
 * submitter answers OR the approver/admin withdraws the request.
 *
 * Shows what was asked + the approver's note so any later viewer (e.g. an
 * admin withdrawing on behalf of the original approver) has full context.
 */
function PausedActionsCard({
	openInfoGate,
	onWithdraw,
	isWithdrawing,
	error,
}: {
	openInfoGate: RequestInfoGateDTO
	onWithdraw: () => void
	isWithdrawing: boolean
	error: Error | null
}) {
	return (
		<Card className="border-amber-300 bg-amber-50">
			<CardHeader>
				<CardTitle className="text-base text-amber-900">Waiting for the submitter to provide more info</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				{openInfoGate.requestedByApproverNote ? (
					<p className="rounded border border-amber-200 bg-white p-3 text-sm text-amber-900">
						<span className="font-medium">Note to submitter:</span> {openInfoGate.requestedByApproverNote}
					</p>
				) : null}
				<div>
					<p className="mb-2 text-xs font-medium uppercase tracking-wide text-amber-900">Fields requested</p>
					<ul className="list-disc space-y-1 pl-5 text-sm text-amber-900">
						{openInfoGate.fieldsSnapshot.map((f) => (
							<li key={f.id}>
								{f.label}
								{f.required ? <span className="text-red-700"> *</span> : null}{' '}
								<span className="text-xs text-amber-700">({f.type})</span>
							</li>
						))}
					</ul>
				</div>
				<div className="flex gap-2 pt-2">
					<Button variant="outline" disabled={isWithdrawing} onClick={onWithdraw}>
						{isWithdrawing ? 'Withdrawing…' : 'Withdraw info request'}
					</Button>
				</div>
				{error ? (
					<p role="alert" className="text-sm text-destructive">
						Could not withdraw the info request — please retry.
					</p>
				) : null}
			</CardContent>
		</Card>
	)
}

// ── Pending state: submitter has answered a previously-raised info-request ─

/**
 * Surfaced when a side-loop info-request was opened earlier and the
 * submitter has now replied. SLA is resumed (`slaPausedAt` is null), so the
 * approver sees Decide actions below as usual — this card adds the answered
 * content above the Decide card so they don't have to navigate to the parent
 * request to see what was answered.
 *
 * Renders the approver's original note (so they remember what they asked for)
 * + each `fieldsSnapshot` field with the corresponding `providedData` value.
 * Until the full-payload query resolves, only the summary line ("Submitter
 * replied · 5m ago · with: foo, bar") is shown; the field+value rows appear
 * once `full` is populated.
 */
function AnsweredInfoCard({
	summary,
	full,
	dataTestId,
}: {
	summary: { id: string; completedAt: string; requestedByApproverNote: string | null; providedDataKeys: string[] }
	full: RequestInfoGateDTO | null
	dataTestId?: string
}) {
	const relative = formatRelativeFromNow(summary.completedAt)
	const fields = full?.fieldsSnapshot ?? []
	const providedData = (full?.providedData ?? {}) as Record<string, unknown>
	return (
		<Card className="border-teal-300 bg-teal-50" data-testid={dataTestId}>
			<CardHeader>
				<CardTitle className="text-base text-teal-900">Information provided by submitter · {relative}</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				{summary.requestedByApproverNote ? (
					<p className="rounded border border-teal-200 bg-white p-3 text-sm text-teal-900">
						<span className="font-medium">You asked:</span> {summary.requestedByApproverNote}
					</p>
				) : null}
				{full ? (
					fields.length > 0 ? (
						<dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
							{fields.map((f) => (
								<div key={f.id}>
									<dt className="text-xs font-medium uppercase tracking-wide text-teal-900">{f.label}</dt>
									<dd className="mt-1 text-sm text-teal-950">{formatProvidedValue(providedData[f.id])}</dd>
								</div>
							))}
						</dl>
					) : (
						<p className="text-sm text-teal-900">
							Replied with: {summary.providedDataKeys.length > 0 ? summary.providedDataKeys.join(', ') : '—'}
						</p>
					)
				) : (
					<p className="text-sm text-teal-900">
						Loading the provided answers… (keys: {summary.providedDataKeys.join(', ') || '—'})
					</p>
				)}
			</CardContent>
		</Card>
	)
}

/** Match `_infoRequestNotice.tsx`'s humanise-iso helper. Inlined here to keep
 *  the card decoupled from that module's specific styling. */
function formatRelativeFromNow(iso: string): string {
	const diffMs = Date.now() - new Date(iso).getTime()
	const minutes = Math.round(diffMs / 60000)
	if (minutes < 1) return 'just now'
	if (minutes < 60) return `${minutes}m ago`
	const hours = Math.round(minutes / 60)
	if (hours < 48) return `${hours}h ago`
	const days = Math.round(hours / 24)
	return `${days}d ago`
}

function formatProvidedValue(raw: unknown): string {
	if (raw === null || raw === undefined || raw === '') return '—'
	if (typeof raw === 'boolean') return raw ? 'Yes' : 'No'
	if (typeof raw === 'string' || typeof raw === 'number') return String(raw)
	try {
		return JSON.stringify(raw)
	} catch {
		return '—'
	}
}

// ── Pending state: full Approve / Reject / Request more info ────────────────

function DecideCard({
	comment,
	setComment,
	busy,
	rejectDisabled,
	onApprove,
	onReject,
	onRequestInfo,
	approveError,
	rejectError,
	requestInfoError,
	approving,
	rejecting,
	requestingInfo,
}: {
	comment: string
	setComment: (next: string) => void
	busy: boolean
	rejectDisabled: boolean
	onApprove: () => void
	onReject: () => void
	onRequestInfo: (payload: { fields: InfoGateField[]; note?: string }) => void
	approveError: Error | null
	rejectError: Error | null
	requestInfoError: Error | null
	approving: boolean
	rejecting: boolean
	requestingInfo: boolean
}) {
	const [dialogOpen, setDialogOpen] = useState(false)
	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">Decide</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				<label className="block text-sm font-medium" htmlFor="approval-comment">
					Comment{' '}
					<span className="text-xs font-normal text-muted-foreground">(optional on approve, required on reject)</span>
				</label>
				<textarea
					id="approval-comment"
					className="border-input bg-background ring-offset-background flex min-h-[100px] w-full rounded-md border px-3 py-2 text-sm"
					value={comment}
					onChange={(e) => setComment(e.target.value)}
					placeholder="Add context for the request submitter…"
				/>
				<div className="flex flex-wrap gap-2">
					<Button disabled={busy} onClick={onApprove}>
						{approving ? 'Approving…' : 'Approve'}
					</Button>
					<Button variant="destructive" disabled={rejectDisabled} onClick={onReject}>
						{rejecting ? 'Rejecting…' : 'Reject'}
					</Button>
					<Button variant="outline" disabled={busy} onClick={() => setDialogOpen(true)}>
						Request more info
					</Button>
				</div>
				{(approveError || rejectError || requestInfoError) && (
					<p role="alert" className="text-sm text-destructive">
						Action failed.{' '}
						{isAxiosError(approveError ?? rejectError ?? requestInfoError)
							? ((
									(approveError ?? rejectError ?? requestInfoError) as {
										response?: { data?: { error?: { message?: string } } }
									}
								).response?.data?.error?.message ?? 'Please retry.')
							: 'Please retry.'}
					</p>
				)}
				{dialogOpen ? (
					<RequestInfoDialog
						submitting={requestingInfo}
						onCancel={() => setDialogOpen(false)}
						onSubmit={(payload) => {
							onRequestInfo(payload)
							setDialogOpen(false)
						}}
					/>
				) : null}
			</CardContent>
		</Card>
	)
}

// ── Request-more-info dialog ────────────────────────────────────────────────

const FIELD_TYPE_OPTIONS: InfoGateField['type'][] = ['text', 'textarea', 'number', 'date', 'checkbox', 'select']

interface DraftField extends InfoGateField {
	/** Stable key for React reconciliation across reorder / removal. */
	_key: string
}

function emptyDraftField(): DraftField {
	return { _key: crypto.randomUUID(), id: '', label: '', type: 'text', required: false }
}

/**
 * Modal-style dialog for composing the side-loop info-request. Inline (not a
 * shadcn `<Dialog>`) — the page is short and the form is in-context. Two
 * sections:
 *  - **Note** (optional, shown to the submitter as the approver's reason)
 *  - **Fields the submitter must fill** — dynamic list with add/remove rows.
 *    Each row has label + type + required checkbox; `id` is auto-derived
 *    from the label (slugified) so the approver doesn't have to think
 *    about the wire format.
 */
function RequestInfoDialog({
	onCancel,
	onSubmit,
	submitting,
}: {
	onCancel: () => void
	onSubmit: (payload: { fields: InfoGateField[]; note?: string }) => void
	submitting: boolean
}) {
	const [note, setNote] = useState('')
	const [drafts, setDrafts] = useState<DraftField[]>(() => [emptyDraftField()])
	const [error, setError] = useState<string | null>(null)

	const updateDraft = (key: string, patch: Partial<DraftField>) => {
		setDrafts((prev) => prev.map((d) => (d._key === key ? { ...d, ...patch } : d)))
	}

	const handleSubmit = () => {
		const fields: InfoGateField[] = []
		for (const d of drafts) {
			const label = d.label.trim()
			if (!label) {
				setError('Every field needs a label.')
				return
			}
			// Auto-id: slugify the label. Backend doesn't care about the
			// shape as long as it's unique within the snapshot — collisions
			// here would silently overwrite providedData on the submitter side.
			const id = label
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, '_')
				.replace(/^_+|_+$/g, '')
			if (!id) {
				setError(`Field "${label}" produces an empty id — try alphanumeric characters.`)
				return
			}
			if (fields.some((f) => f.id === id)) {
				setError(`Field "${label}" duplicates another field's id ("${id}"). Rename one.`)
				return
			}
			fields.push({ id, label, type: d.type, required: d.required })
		}
		if (fields.length === 0) {
			setError('Add at least one field.')
			return
		}
		setError(null)
		onSubmit({ fields, note: note.trim() || undefined })
	}

	return (
		<div
			role="dialog"
			aria-label="Request more information from the submitter"
			className="rounded-md border bg-background p-4 shadow-md"
		>
			<h2 className="mb-3 text-base font-semibold">Request more information</h2>
			<div className="space-y-3">
				<div>
					<label htmlFor="info-request-note" className="mb-1 block text-sm font-medium">
						Note to the submitter (optional)
					</label>
					<textarea
						id="info-request-note"
						value={note}
						onChange={(e) => setNote(e.target.value)}
						rows={2}
						className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
						placeholder="Why you need this — e.g. 'Please share the project code so I can verify the budget'"
					/>
				</div>
				<div>
					<p className="mb-2 text-sm font-medium">Fields the submitter must fill</p>
					<ul className="space-y-2" data-testid="info-request-fields">
						{drafts.map((d, idx) => (
							<li key={d._key} className="flex flex-wrap items-center gap-2 rounded border p-2">
								<Input
									aria-label={`Field ${idx + 1} label`}
									value={d.label}
									onChange={(e) => updateDraft(d._key, { label: e.target.value })}
									placeholder="Label (e.g. Project code)"
									className="min-w-[180px] flex-1"
								/>
								<select
									aria-label={`Field ${idx + 1} type`}
									value={d.type}
									onChange={(e) => updateDraft(d._key, { type: e.target.value as InfoGateField['type'] })}
									className="border-input bg-background rounded-md border px-2 py-1 text-sm"
								>
									{FIELD_TYPE_OPTIONS.map((t) => (
										<option key={t} value={t}>
											{t}
										</option>
									))}
								</select>
								<label className="flex items-center gap-1 text-xs">
									<input
										type="checkbox"
										checked={Boolean(d.required)}
										onChange={(e) => updateDraft(d._key, { required: e.target.checked })}
									/>
									required
								</label>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => setDrafts((prev) => (prev.length > 1 ? prev.filter((p) => p._key !== d._key) : prev))}
									disabled={drafts.length === 1}
									aria-label={`Remove field ${idx + 1}`}
								>
									Remove
								</Button>
							</li>
						))}
					</ul>
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="mt-2"
						onClick={() => setDrafts((prev) => [...prev, emptyDraftField()])}
					>
						+ Add field
					</Button>
				</div>
				{error ? (
					<p role="alert" className="text-sm text-destructive">
						{error}
					</p>
				) : null}
				<div className="flex justify-end gap-2 pt-2">
					<Button variant="outline" onClick={onCancel} disabled={submitting}>
						Cancel
					</Button>
					<Button onClick={handleSubmit} disabled={submitting}>
						{submitting ? 'Sending…' : 'Send request'}
					</Button>
				</div>
			</div>
		</div>
	)
}

// ── Misc UI ──────────────────────────────────────────────────────────────────

function DescTerm({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
			<dd className="mt-1 break-words font-mono text-xs">{value}</dd>
		</div>
	)
}

function DetailSkeleton() {
	return (
		<div className="mx-auto max-w-2xl space-y-3">
			<div className="h-7 w-1/2 animate-pulse rounded bg-muted" />
			<div className="h-48 animate-pulse rounded-lg border bg-muted/40" />
		</div>
	)
}

function ErrorState({ message }: { message: string }) {
	return (
		<div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
			{message}
		</div>
	)
}
